import { displayCents, fmtMoney, fmtPct } from './format.js';
import type { InvoiceVerdict, PLReport, VolumeFlag } from './types.js';

/**
 * The plain functions behind the P&L card and the tiles (P7/P8/P9/P12), kept
 * out of the components so the words a number is read under are unit-tested:
 * a wrong label on a right figure is still a wrong dashboard.
 *
 * Invariants:
 *  - a label never claims more than the data source does (R29): "provider-
 *    reported" only when the server says EVERY row carried stored coverage;
 *  - the four-layer tie is checked from the NUMBERS on the report, never
 *    taken from prose, and reads "off by" with the true sign;
 *  - money is never computed here - the charge, the variance, the verdict all
 *    come from the server; these helpers only word them.
 */

/**
 * The Coverage tile's sub-label, honest to `coverageSource`: 'provider' = the
 * searches-weighted mean of stored coverage; 'partly' = monetized ÷ searches
 * with the rows that lack a stored figure counted (or "some" until the server
 * says how many); 'calculated' = monetized ÷ searches; null = nothing.
 */
export function coverageLabel(
  source: 'provider' | 'partly' | 'calculated' | null,
  calculatedRows?: number | undefined,
): string {
  switch (source) {
    case 'provider':
      return 'provider-reported';
    case 'partly':
      return calculatedRows != null && calculatedRows > 0
        ? `monetized ÷ searches (${calculatedRows} row${calculatedRows === 1 ? ' lacks' : 's lack'} provider coverage)`
        : 'monetized ÷ searches (some rows lack provider coverage)';
    case 'calculated':
      return 'monetized ÷ searches';
    default:
      return '';
  }
}

/** Half a cent: the same threshold the server's `ties` uses. */
const TIE_TOLERANCE = 0.005;

/**
 * The reasons the layers do not tie, in the order a reader would check them;
 * empty when they all hold. Computed from the report's own numbers so the
 * sentence can never disagree with the figures above it.
 */
export function tieProblems(
  pl: Pick<PLReport, 'operating' | 'matched' | 'matchingGaps' | 'overheads' | 'reconciliation'>,
): string[] {
  const out: string[] = [];
  const rec = pl.reconciliation;
  const fourLayer = pl.operating.net - (pl.matched.net + pl.matchingGaps.net + pl.overheads.net);
  if (Math.abs(fourLayer) >= TIE_TOLERANCE)
    out.push(`Matched + Matching gaps + Overheads is ${fmtMoney(fourLayer)} from Operating`);
  if (Math.abs(rec.difference) >= TIE_TOLERANCE)
    out.push(`Matched + Unattributed is ${fmtMoney(rec.difference)} from Operating`);
  if (Math.abs(rec.attributionDifference ?? 0) >= TIE_TOLERANCE) {
    out.push(
      `attributed spend is ${fmtMoney(rec.attributionDifference)} from Σ confirmed links (a provisional link outranking a confirmed one?)`,
    );
  }
  return out;
}

/**
 * The month the statement picker starts on: the month of the window's last
 * day (P12).
 */
export function defaultStatementMonth(upto: string): string {
  return /^\d{4}-\d{2}/.test(upto) ? upto.slice(0, 7) : '';
}

/**
 * The LATEST month the picker may choose: the month of yesterday, the last
 * complete day - a later month has no statement yet and the server refuses
 * it (E7). Not the window's month: under "2d ago" on the 2nd the window ends
 * in the previous month while the 1st is already complete.
 */
export function latestStatementMonth(today: string): string {
  const d = new Date(`${today}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return '';
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 7);
}

/**
 * The Cost tile's sub-label, honest to what the tile sums (E11): confirmed
 * buy-side spend through confirmed links, plus - unless the owner confirmed
 * the ingested columns as the Python model - the ingested cost on EVERY row
 * in range, linked or not (read/data.ts sums it wherever it sits; the P&L
 * splits it by confirmed spend, so "+ ingested" must not read as
 * link-filtered). Until the P&L (which carries the setting) has loaded, the
 * label claims nothing about the ingested part rather than guess. The
 * launch line and the cost-basis note follow.
 */
export function costTileLabel(opts: {
  semantics: 'unconfirmed' | 'real' | 'model' | undefined;
  launchCost: number | null | undefined;
  launchCostPer1k: number | undefined;
  costBasis: 'airman' | 'undetect' | undefined;
}): string {
  const base =
    opts.semantics === 'model' || opts.semantics === undefined
      ? 'confirmed buy-side'
      : 'confirmed buy-side + ingested cost on every row';
  const launch = opts.launchCost
    ? ` · incl. launch ${fmtMoney(opts.launchCost)} at $${(opts.launchCostPer1k ?? 0).toFixed(2)}/1K committed`
    : '';
  const basis = opts.costBasis === 'undetect' ? ' · the launcher bill is in the P&L below' : '';
  return base + launch + basis;
}

/**
 * The Cost tile once the P&L has loaded (P2e): with no filter set its value
 * is the OPERATING cost - the launcher bill (or Airman's spend on the airman
 * basis), the hosting bill and the ingested cost once confirmed real - the
 * cost of the business, not the slice attributed through confirmed links.
 * The sub-line names each part and says how much of it is attributed. With a
 * filter set the operating cost cannot follow it, so the tile shows the
 * attributed spend and says the operating cost ignores filters. Before the
 * P&L lands the tile keeps its old shape. `usesOperating` tells the caller
 * whether a delta chip against the previous window's ATTRIBUTED spend would
 * compare like with like (it would not).
 */
export function costTileView(opts: {
  pl: Pick<PLReport, 'operating' | 'ingestedCost' | 'costBasis'> | undefined;
  tCost: number | null;
  filtered: boolean;
  launchCost: number | null | undefined;
  launchCostPer1k: number | undefined;
}): { value: string; sub: string; usesOperating: boolean } {
  const attributed = opts.tCost != null ? fmtMoney(opts.tCost) : '—';
  if (!opts.pl) {
    return {
      value: attributed,
      sub: costTileLabel({
        semantics: undefined,
        launchCost: opts.launchCost,
        launchCostPer1k: opts.launchCostPer1k,
        costBasis: undefined,
      }),
      usesOperating: false,
    };
  }
  if (opts.filtered) {
    // Under a filter the figure is the attributed cost for that slice. Say
    // whether the modelled launch line is inside it (a rate is set) or that
    // the slice cannot be costed at all (a per-country day is unknown), so a
    // lower bound never reads as the total.
    const launch =
      opts.tCost == null
        ? ' · unknown for this slice on some days (no per-country buy-side rows or counts)'
        : (opts.launchCostPer1k ?? 0) > 0
          ? ' · incl. the modelled launch line'
          : '';
    return {
      value: attributed,
      sub: `attributed through confirmed links only${launch} · the operating cost ignores filters — see the P&L below`,
      usesOperating: false,
    };
  }
  const o = opts.pl.operating;
  const parts = [
    `${opts.pl.costBasis === 'undetect' ? 'launcher bill' : 'Airman spend'} −${fmtMoney(o.cost)}`,
  ];
  if (o.hostingCost > 0) parts.push(`hosting −${fmtMoney(o.hostingCost)}`);
  if (o.ingestedCost > 0) parts.push(`ingested −${fmtMoney(o.ingestedCost)}`);
  // While the ingested columns are unconfirmed the attributed figure still
  // counts them (the tiles always did) but the operating cost does not, so
  // "of which" would not hold: say what the figure carries instead.
  const unconfirmedIngested =
    opts.pl.ingestedCost.semantics === 'unconfirmed' && opts.pl.ingestedCost.rows > 0;
  parts.push(
    opts.tCost == null
      ? 'none attributed — no confirmed links in range'
      : unconfirmedIngested
        ? `${fmtMoney(opts.tCost)} attributed through confirmed links, incl. ingested cost the P&L does not charge (unconfirmed)`
        : `of which ${fmtMoney(opts.tCost)} attributed through confirmed links`,
  );
  // The total is the SUM OF THE DISPLAYED PARTS (each rounded to the cent as
  // the sub-line prints it), so the tile always adds up on the page - the
  // exact components can round to .21 and .62 while their exact sum rounds
  // to .82. The operating net itself is untouched.
  return {
    value: fmtMoney(
      (displayCents(o.cost) + displayCents(o.hostingCost) + displayCents(o.ingestedCost)) / 100,
    ),
    sub: parts.join(' · '),
    usesOperating: true,
  };
}

/**
 * The hero tile's sub-line once the P&L has loaded (P2e): the MATCHED layer -
 * matched net on the linked revenue, its margin, and how many feeds carry a
 * confirmed link - never "all revenue minus the attributed cost", which read
 * as a business margin while it was neither.
 */
export function heroSub(opts: {
  matched: Pick<PLReport['matched'], 'net' | 'revenue' | 'feeds'>;
  filtered: boolean;
}): string {
  const m = opts.matched;
  const tail = opts.filtered ? ' · operating net ignores the filters' : '';
  if (m.feeds === 0) return 'no confirmed links in range' + tail;
  const margin = m.revenue > 0 ? fmtPct((100 * m.net) / m.revenue) : '—';
  return `matched net ${fmtMoney(m.net)} on ${fmtMoney(m.revenue)} linked revenue · ${margin} margin · ${m.feeds} feed${m.feeds === 1 ? '' : 's'} with confirmed links${tail}`;
}

/**
 * The headline tile on Stats, and which question it answers (owner report
 * 2026-09-22: "on the operating net when you select a feed provider it still
 * shows overall net").
 *
 * Unfiltered it is the OPERATING net: revenue against the bills, top-down,
 * independent of matching - the truest figure the app has, and the one the
 * P&L ties to.
 *
 * Under ANY filter it becomes the MATCHED net for that slice: the filtered
 * revenue less the cost its confirmed links carry. The operating net cannot
 * be sliced by rep, source, feed, country or type - it contains the launcher
 * bill beyond what Airman attributes, hosting, the fixed monthly costs and
 * revenue no link claims, none of which belong to one provider without
 * inventing an allocation - so the tile answers what the filter asked and
 * hands the whole-business figure to the line underneath. Before, the number
 * simply did not move while Revenue and Cost beside it did, with a tail on
 * the sub-line the only sign.
 *
 * `value` is null only when a filtered slice has revenue but no computable
 * cost (a country with no per-country buy-side rows): the tile shows a dash
 * and says why, rather than printing revenue as if it were profit.
 */
export interface HeroView {
  kind: 'operating' | 'matched';
  label: string;
  value: number | null;
  sub: string;
  /** the phone's shorter line; '' when there is nothing worth the width. */
  subShort: string;
  /** the same figure over the window before this one, when there is one to compare. */
  prior: number | null;
}

export function heroView(opts: {
  filtered: boolean;
  pl: Pick<PLReport, 'operating' | 'matched' | 'prior'>;
  /** the FILTERED totals of the window, and of the one before it. */
  totals: { revenue: number; profit: number | null; cost: number | null; costUnknown: number };
  prior?: { profit: number | null } | null;
  /** names the filter in the sub-line, e.g. 'Xaria'; omitted when there are several. */
  what?: string | null;
  /** a country filter is the one that can leave cost unknown - it explains the dash. */
  countryFiltered?: boolean;
}): HeroView {
  const { pl, totals } = opts;
  if (!opts.filtered) {
    return {
      kind: 'operating',
      label: 'Operating net',
      value: pl.operating.net,
      sub: heroSub({ matched: pl.matched, filtered: false }),
      subShort:
        pl.operating.revenue > 0
          ? `${fmtPct((100 * pl.operating.net) / pl.operating.revenue)} of revenue`
          : '',
      prior: pl.prior ? pl.prior.operatingNet : null,
    };
  }
  const margin =
    totals.profit != null && totals.revenue > 0
      ? `${fmtPct((100 * totals.profit) / totals.revenue)} margin · `
      : '';
  const whole = `operating net ${fmtMoney(pl.operating.net)} for the window, all feeds`;
  const why =
    totals.profit == null
      ? opts.countryFiltered
        ? 'cost unknown for this country on some days (no per-country buy-side rows) · '
        : 'no confirmed buy-side links in range · '
      : '';
  const unknown =
    totals.costUnknown > 0
      ? `${totals.costUnknown} buy-side row${totals.costUnknown === 1 ? '' : 's'} with an unreadable cost counted as $0 · `
      : '';
  return {
    kind: 'matched',
    // Says what it is AND that it is a slice: the number beside a filter must
    // never read as the whole business.
    label: opts.what ? `Net · ${opts.what}` : 'Net · filtered',
    value: totals.profit,
    sub: `${why}${margin}${unknown}revenue less the cost on confirmed links · ${whole}`,
    // The phone keeps the two facts that matter: the margin of the slice and
    // that the whole-business figure is a different number.
    subShort: `${margin}${whole}`,
    prior: opts.prior?.profit ?? null,
  };
}

export const VOLUME_LABEL: Record<VolumeFlag, string> = {
  ok: 'volume ok',
  gap: 'launch gap',
  over: 'launch over',
  none: 'no launches',
  lag: 'no verdict yet',
};

export const VOLUME_TITLE: Record<VolumeFlag, string> = {
  ok: "the share of the day's launches Airman attributes to destinations is within tolerance of the window's usual share",
  gap: "fewer of the day's launches are attached to a destination than usual for this window (tolerance points below the usual share), or Airman saw none - even where the money agrees",
  over: "Airman registered more visits than the bill's launches, beyond tolerance - its registrations are wrong",
  none: 'the bill has no launches for this day',
  lag: 'the bill has launches but Airman has not reported this far yet - no volume verdict until it does (E3)',
};

/** 'lag' and 'none' are warnings (nothing to compare yet / nothing to compare); gap and over are errors. */
export function volumeTone(flag: VolumeFlag): 'ok' | 'warn' | 'error' {
  return flag === 'ok' ? 'ok' : flag === 'none' || flag === 'lag' ? 'warn' : 'error';
}

export const VERDICT_LABEL: Record<InvoiceVerdict, string> = {
  ok: 'ok',
  over: 'invoice over the mirror',
  under: 'invoice under the mirror',
  unmirrored: 'no mirrored day',
};

export function verdictTone(v: InvoiceVerdict): 'ok' | 'warn' | 'error' {
  return v === 'ok' ? 'ok' : v === 'unmirrored' ? 'warn' : 'error';
}

/**
 * A list out of a response body, whichever way the route wrapped it: a bare
 * array, or `{ items }` / `{ <key> }`. Anything else reads as empty rather
 * than crashing the card - the contract fixes the row shape, not the wrapper.
 */
export function listOf<T>(body: unknown, ...keys: string[]): T[] {
  if (Array.isArray(body)) return body as T[];
  if (typeof body === 'object' && body !== null) {
    for (const k of ['items', ...keys]) {
      const v = (body as Record<string, unknown>)[k];
      if (Array.isArray(v)) return v as T[];
    }
  }
  return [];
}

/**
 * The P7 banner's sentence, or null when there is nothing to decide: the
 * columns are confirmed either way, or no row carries them.
 */
export function ingestedBanner(state: {
  semantics: string;
  rows: number;
  total: number;
}): string | null {
  if (state.semantics !== 'unconfirmed' || !(state.rows > 0)) return null;
  return `${state.rows} row${state.rows === 1 ? '' : 's'} carr${state.rows === 1 ? 'ies' : 'y'} ${fmtMoney(state.total)} of ingested costs whose meaning is unconfirmed — decide under Configuration → Buy side`;
}
